"use client";

import styled from "styled-components";
import { Dropdown, DropdownSelectEvent } from "../Dropdown/Dropdown";
import UploadIcon from "@/assets/icons/UploadIcon.svg";
import DeleteIcon from "@/assets/icons/DeleteIcon.svg";
import ZippedFolder from "@/assets/icons/ZippedFolderIcon.svg";
import EncryptHelp from "@/assets/photos/EncryptFiles.png";
import Image from "next/image";
import { TextField } from "../TextField/TextField";
import { FileRejection, FileWithPath, useDropzone } from "react-dropzone";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import theme from "@/themes/velys-theme";
import { useEffect, useState } from "react";
import Button from "../Button/Button";
import toast from "react-hot-toast";
import { UploadMutationType } from "@/lib/hooks/useCaseUpload";
import { useTranslations } from "next-intl";

interface UploadCaseDataContentProps {
  uploadMutation: UploadMutationType;
}

//#region styled
const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  font-size: 1.125rem;

  .instruction-main {
    font-weight: bold;

    span {
      color: ${(props) => props.theme.main};
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .encrypt {
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: center;

    .help {
      font-size: 1rem;
      cursor: pointer;
      color: ${(props) => props.theme.main};
    }
  }

  .buttons {
    display: flex;
    gap: 15px;
    width: 100%;
    justify-content: flex-end;
  }
`;

const DragDropSection = styled.div`
  min-width: 341px;
  margin-top: 10px;
  min-height: 6.25em;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.main};
  border: 1px dashed ${(props) => props.theme.main};
  font-size: 16px;
  padding: 15px 50px;
  position: relative;
  background-color: ${(props) => props.theme.backgroundGrey};

  input {
    z-index: 5;
    width: 100% !important;
    height: 100% !important;
  }

  .dragdrop {
    color: black;
  }

  .dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 100%;
    text-align: center;
    cursor: pointer;
  }

  .folder-file {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 30px;
    word-break: break-all;
    font-weight: bold;
    width: 100%;

    .content {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .remove {
      padding: 5px 3px !important;
      border-radius: 3px !important;
    }
  }
`;

const HelpView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 720px;

  img {
    height: 100%;
    width: 100%;
  }
`;
//#endregion

export default function UploadCaseDataContent({
  uploadMutation,
}: UploadCaseDataContentProps) {
  const t = useTranslations("toast");
  const tContent = useTranslations("modals.uploadCaseData");
  const orgLocations = useBoundStore((state) => state.orgLocations);
  const setCaseUploadStatus = useBoundStore(
    (state) => state.setCaseUploadStatus,
  );
  const setModalType = useBoundStore((state) => state.setModalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);

  const caseUploadLocation = useBoundStore((state) => state.caseUploadLocation);
  const setCaseUploadLocation = useBoundStore(
    (state) => state.setCaseUploadLocation,
  );
  const setUploadProgress = useBoundStore((state) => state.setUploadProgress);
  const [file, setFile] = useState<File[]>([]);
  const [encryptKey, setEncryptKey] = useState("");
  const viewingHelp = useBoundStore((state) => state.viewingHelp);
  const setViewingHelp = useBoundStore((state) => state.setViewingHelp);
  const userCountry = useBoundStore((state) => state.userCountry);
  const isEmea = ["GB", "AT", "CH", "DE"].includes(userCountry);
  // const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  //reset upload status
  useEffect(() => {
    setUploadProgress(0);
    setCaseUploadStatus(null);
    setCaseUploadLocation("");
  }, []);

  const handleLocationSelect = (e: DropdownSelectEvent) => {
    const value = (e.target as HTMLLIElement).getAttribute("data-value");
    setCaseUploadLocation(value!);
  };

  const setEncryption = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEncryptKey(e.target.value);
  };

  const { open, getRootProps, getInputProps } = useDropzone({
    accept: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
    noClick: true,
    noKeyboard: true,
    maxFiles: 1, // only allow 1 file
    onDrop: async (files: File[]) => {
      setFile(files);
    },
    onDropRejected: (fileRejections: FileRejection[]) => {
      console.log(fileRejections);
      //   if (fileRejections.length > 1) {
      //     toast(
      //       "More than 1 file selected, please only select 1 file for upload"
      //     );
      //   } else if (fileRejections[0].file.size > MAX_FILE_SIZE) {
      //     toast("File is too large for upload");
      //   }
    },
  });

  const acceptedFileItems = file.map((folder: FileWithPath) => {
    return (
      <div key={folder.path} className="folder-file">
        <div className="content">
          <Image height={30} width={30} src={ZippedFolder} alt="zipped-icon" />
          {folder.name}
        </div>
        <Image
          height={20}
          width={20}
          alt="delete-file"
          style={{ cursor: "pointer" }}
          src={DeleteIcon.src}
          onClick={() => {
            setFile([]);
          }}
        />
      </div>
    );
  });

  const handleUploadZip = async () => {
    if (!caseUploadLocation) {
      toast(t("uploadCase.select_location"));
      return;
    }
    if (!encryptKey) {
      toast(t("uploadCase.enter_encryption_key"));
      return;
    }

    if (file.length === 0) {
      toast(t("uploadCase.select_zip_file"));
      return;
    }

    //testing only
    // handleTestUpload();

    uploadMutation.mutate({
      file: file[0],
      encryptKey,
      location_name: caseUploadLocation,
    });
  };

  return (
    <UploadContent>
      {!viewingHelp ? (
        <>
          <p className="instruction-main">
            {tContent("toLearn")}&nbsp;
            <a
              href={tContent(isEmea ? "emeaPdfLink" : userCountry === "AU" ? "anzPdfLink" : "usPdfLink")}
              target="_blank"
            >
              <span>{tContent("tutorial")}</span>
            </a>
          </p>
          <p style={{ marginBottom: "10px" }}>{tContent("step1")}</p>
          <Dropdown
            inModal
            dropdownTitle={tContent("locations.label")}
            options={orgLocations.filter((loc) => loc.value !== "all")}
            onSelect={(e) => {
              handleLocationSelect(e);
            }}
            color={theme.main}
            backgroundColor={theme.white}
            selectedOption={
              caseUploadLocation || tContent("locations.defaultValue")
            }
          />
          <p>{tContent("step2")}</p>
          <DragDropSection>
            {file.length === 0 ? (
              <div {...getRootProps({ className: "dropzone" })}>
                <input {...getInputProps()} />
                <p className="dragdrop">{tContent("dragDrop")}</p>
                <Button
                  label={tContent("dropzoneButton")}
                  name="open"
                  onPress={open}
                  theme="primary"
                  icon={UploadIcon.src}
                />
              </div>
            ) : (
              acceptedFileItems
            )}
          </DragDropSection>
          <p style={{ marginBottom: "10px" }}>{tContent("finalStep")}</p>
          <div className="encrypt">
            <TextField
              placeholder={tContent("placeholder")}
              title={tContent("label")}
              color={theme.main}
              backgroundColor={theme.white}
              valueChangeHandler={setEncryption}
              currentValue={encryptKey}
            />
            <p className="help" onClick={() => setViewingHelp(true)}>
              {tContent("whereDoI")}
            </p>
          </div>
          <div className="buttons">
            <Button
              label={tContent("cancel")}
              onPress={() => {
                setModalOpen(false);
                setModalType("");
              }}
              theme="secondary"
              name="cancel"
            />
            <Button
              label={tContent("upload")}
              onPress={handleUploadZip}
              theme="primary"
              name="upload"
            />
          </div>
        </>
      ) : (
        <HelpView>
          <p>{tContent("help")}</p>
          <Image
            alt="encryption-help"
            src={EncryptHelp.src}
            height={1000}
            width={789}
            style={{ height: "auto" }}
          />
          <div className="buttons">
            <Button
              label={tContent("returnToUpload")}
              onPress={() => setViewingHelp(false)}
              theme="primary"
              name="upload"
            />
          </div>
        </HelpView>
      )}
    </UploadContent>
  );
}
